/**
 * League reference: the rulebook, the scoring, the Grid, the money.
 *
 * These are the four things somebody goes looking for mid-argument, and until now three of them
 * lived in a Google Doc and the fourth lived in Yahoo. The point of putting them here is that a
 * citation chip anywhere else in the app can eventually deep-link into this page.
 *
 * The rulebook view is the important one. 141 rules across 14 years, many superseded — showing
 * them undifferentiated would be worse than the Doc. So: search, section filter, and status is
 * a first-class visual, with superseded rules hidden by default and one click away.
 */

import { useState } from 'react';
import { faab } from '../domain/faab.js';
import leagueSettings from '../data/league-settings-2026.json';
import { LEAGUE_2026 } from '../domain/config.js';
import { roundBand } from '../domain/picks.js';
import {
  PUBLISHED_MAX_KEEPER_LENGTH, escalationChain, maxKeeperYears, penaltyRound, udfaRoundValue,
} from '../domain/grid.js';
import { Citations, Linkify } from './RuleText.js';
import { Records } from './Records.js';
import { Trades } from './Trades.js';
import { Rules } from './Rules.js';
import { Faab as FaabScreen } from './Faab.js';
import {
  type LeagueStore, type Role,
} from '../store/types.js';



export function League(
  { view, gm, actor, role, store, focusRule }:
  { view: 'rules' | 'scoring' | 'grid' | 'faab' | 'records' | 'trades';
    gm: string; actor: string; role: Role; store: LeagueStore; focusRule?: string },
) {
  if (view === 'trades') return <Trades gm={gm} actor={actor} role={role} store={store} />;
  if (view === 'scoring') return <Scoring />;
  if (view === 'grid') return <Grid />;
  if (view === 'records') return <Records role={role} />;
  if (view === 'faab') return <FaabScreen gm={gm} role={role} store={store} />;
  /*
   * Rules moved out to `Rules.tsx` when it became the destination for every `§` in the app — it
   * carries a rail, eleven groups, the Grid and Scoring as panes, and hash arrival on a rule id,
   * none of which belonged in a file that also holds three other screens. `focusRule` is the
   * third hash segment.
   */
  return <Rules gm={gm} role={role} store={store} focusRule={focusRule} />;
}



  /*
   * `Rulebook` used to live here — a flat, ungrouped, searchable accordion of all 141 rules.
   * It moved to `Rules.tsx` on 22 August and grew a rail, eleven derived topic groups, the Grid
   * and Scoring as first-class panes, and a DOM id per rule so the twenty reserved `§` links
   * resolve. The interpretation panels and the codify-to-proposal bridge moved with it; that
   * bridge is the only path in the app that creates a `codify-*` draft proposal.
   */

  /* -- scoring ------------------------------------------------------------------------------- */

  const SET = leagueSettings as {
    settings: string[][]; offense: string[][]; defense: string[][]; source: string;
  };


  /* -- the grid ------------------------------------------------------------------------------ */

  /**
   * The Grid (`14a`).
   *
   * REBUILT AROUND THE ROUND HE WAS DRAFTED IN, not the round he is currently valued at, because
   * that is the number a manager actually holds: he knows he took Puka in the eleventh, and the
   * question is what keeping him costs and for how long. The old table answered the same
   * arithmetic from the engine's end and made everybody translate.
   *
   * The trace is the drawing's own idea and the best thing on the page: pick a round and watch
   * one player's whole keeper life fall out of it — `16 → 12 → 8 → 5 → 2 → not eligible`. Five
   * years of a rule that nobody can hold in their head, in one line of chips.
   */


/*
 * The old `Faab` lived here — a balances bar, a flat ledger table and a "record a movement" form
 * with a free-text kind and a typed amount. It is replaced by `Faab.tsx`, built from
 * `FFL Pass 06 FAAB.dc.html`: the GM table with sparklines, the stretched detail panel, and an
 * entry bar whose fields change with the kind — so a purchase DERIVES its scrip at the fixed
 * $2:₣1 rate and a fine cannot have its amount typed at all.
 */

/*
 * SCORING AND THE GRID LIVE AT MODULE SCOPE (Tim, 9 September: "scroll is bouncing back to top
 * after scrolling down").
 *
 * That report was the player list, and the cause was a component declared inside a render body:
 * a function declared in a body is a new function object every render, React sees a new element
 * TYPE, and a new type is an unmount and a remount rather than an update. These two were the same
 * shape. Nothing visible was going wrong here — `League` has no clock ticking under it — but
 * `Grid` keeps its own `useState` for which round is being traced, and any re-render of `League`
 * would have thrown that selection away with the rest of the subtree.
 *
 * They close over nothing, so they belong out here. The rule: never declare a component inside
 * another component. If it needs the parent's state it is a render function, called rather than
 * mounted; if it does not, it lives at module scope, like these.
 */

function Scoring() {
  return (
    <div className="app">
     <Linkify>
      <header className="pagehead">
        <div>
          <h1>Scoring &amp; settings</h1>
          <p className="sub">
            Read from Yahoo, annotated with the rule behind each number.
          </p>
        </div>
      </header>

      <div className="cardrow two">
        <section className="card">
          <h3 className="striphead">Offense</h3>
          <table>
            <thead><tr><th>Event</th><th className="num">Value</th><th>Notes</th></tr></thead>
            <tbody>
              {SET.offense.map(([k, v, n]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="num strong">{v}</td>
                  <td className="mut small">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>

        <section className="card">
          <h3 className="striphead">Defense / Special teams</h3>
          <table>
            <thead><tr><th>Event</th><th className="num">Value</th><th>Notes</th></tr></thead>
            <tbody>
              {SET.defense.map(([k, v, n]) => (
                <tr key={k}>
                  <td>{k}</td>
                  <td className="num strong">{v}</td>
                  <td className="mut small">{n}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      </div>

      <section className="card">
        <h3 className="striphead">League settings</h3>
        <table>
          <tbody>
            {SET.settings.map(([k, v]) => (
              <tr key={k}><td className="mut">{k}</td><td>{v}</td></tr>
            ))}
          </tbody>
        </table>
        <p className="note">{SET.source}</p>
      </section>

      <p className="note">
        Rules behind these numbers: <Citations ids={[
          '2013-IV-1', '2016-IV-1', '2015-IV-2', '2014-IV-2', '2022-dst-scoring',
          '2023-first-down-scoring',
        ]} />
      </p>
     </Linkify>
    </div>
  );
}

function Grid() {
  const rows = Array.from({ length: LEAGUE_2026.draftRounds }, (_, i) => i + 1);
  const [trace, setTrace] = useState<number>(LEAGUE_2026.draftRounds);
  const chain = escalationChain(trace);
  const udfa = udfaRoundValue(LEAGUE_2026);

  return (
    <div className="app">
      <header className="pagehead">
        <div>
          <h1>The Grid</h1>
          <p className="sub">
            What it costs to keep a player, by the round you drafted him in — and how long you
            can hold him before the escalation runs out.
          </p>
        </div>
        <Citations ids={['2019-II-1']} />
      </header>

      {/*
        Trace a player. The table below is the whole rule; this is one row of it walked to the
        end, which is the only form in which "four keeper years" means anything.
      */}
      <section className="card tracecard">
        <div className="striptop">
          <h3 className="striphead">Trace a player</h3>
          <label className="inline">
            <span className="mut">drafted in</span>
            <select value={trace} onChange={(e) => setTrace(Number(e.target.value))}>
              {rows.map((r) => (
                <option key={r} value={r}>
                  Round {r}{r === LEAGUE_2026.draftRounds ? ' · last' : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="tracesum">
          {chain.length === 0
            ? <>Rounds 1 and 2 cannot be kept at all — there is no earlier pick left to forfeit.</>
            : <><b>{maxKeeperYears(trace)} keeper year{maxKeeperYears(trace) === 1 ? '' : 's'}</b>,
              then he is a Round {chain.at(-1)} value and cannot be kept again.</>}
        </p>
        <ol className="tracechain">
          <li className="start"><b>{trace}</b><span>drafted</span></li>
          {chain.map((c, i) => (
            <li key={c}><b>{c}</b><span>year {i + 1}</span></li>
          ))}
          <li className="end"><b>—</b><span>not eligible</span></li>
        </ol>
      </section>

      <div className="cardrow two">
        <section className="card">
          <div className="striptop">
            <h3 className="striphead">Every round</h3>
            <span className="mut">the {LEAGUE_2026.draftRounds}-round draft</span>
          </div>
          <table className="gridtable">
            <thead>
              <tr>
                <th className="num">Round drafted</th>
                <th className="num">Costs you</th>
                <th>Keeper years</th>
                <th className="num">Max length</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const cost = penaltyRound(r);
                const years = maxKeeperYears(r);
                const band = roundBand(r);
                return (
                  <tr key={r} className={cost === null ? 'dim' : ''}>
                    <td className="num strong">
                      {r}
                      {/*
                        The badge is the drawing's, and it earns its place: rounds 10 and later
                        are where the sleeper reward lives, and it is the difference between
                        `14 → 10` and `14 → 14` — a whole extra year at the same price.
                      */}
                      {band === 'sleeper' && (
                        <span className="chip tiny sleeperchip"
                          title="Sleeper-eligible if never dropped">SLEEPER</span>
                      )}
                    </td>
                    <td className="num">
                      {cost === null
                        ? <span className="bad">Not eligible</span>
                        : `Round ${cost}`}
                    </td>
                    <td>
                      <span className="yeardots" aria-hidden="true">
                        {Array.from({ length: years }, (_, i) => <i key={i} />)}
                      </span>
                      <span className="mut yearstext">
                        {years === 0 ? '—' : `${years} year${years === 1 ? '' : 's'}`}
                      </span>
                    </td>
                    <td className="num mut">{PUBLISHED_MAX_KEEPER_LENGTH[r] ?? '—'}</td>
                  </tr>
                );
              })}
              {/*
                After the draft. A free agent is not on the drawn table above him because he was
                never drafted, and leaving him off entirely is how the question "what does a
                waiver pickup cost?" ends up in Discord every September.
              */}
              <tr className="fa">
                <th scope="row">Free agent <span className="mut">picked up in season</span></th>
                <td className="num">Round {udfa}</td>
                <td>
                  <span className="yeardots" aria-hidden="true">
                    {Array.from({ length: maxKeeperYears(udfa) }, (_, i) => <i key={i} />)}
                  </span>
                  <span className="mut yearstext">{maxKeeperYears(udfa)} years</span>
                </td>
                <td className="num mut">{PUBLISHED_MAX_KEEPER_LENGTH[udfa] ?? '—'}</td>
              </tr>
            </tbody>
          </table>
          <p className="railfoot">
            Max length is derived by walking the escalation to its end and cross-checked against
            the published column. 2019-II-1 §2
          </p>
        </section>

        <div className="stack">
          <section className="card">
            <h3 className="striphead">Sleeper reward</h3>
            <p className="note">
              A player drafted in round {LEAGUE_2026.sleeperMinRound} or later and never dropped
              keeps his round for his first keeper year instead of escalating. It defers the
              chain rather than shortening it.
            </p>
            <div className="sleepercmp">
              <div>
                <span className="mut">without it</span>
                <b>14 → {penaltyRound(14) ?? '—'}</b>
              </div>
              <div className="on">
                <span className="mut">as a sleeper</span>
                <b>14 → 14</b>
              </div>
            </div>
            <ul className="conds">
              <li className="yes">Round {LEAGUE_2026.sleeperMinRound} or later</li>
              <li className="yes">Never dropped</li>
              <li className="no">Not already on NFL IR when drafted</li>
              <li className="no">Not a farm player, and not a free agent</li>
            </ul>
            <Citations ids={['2017-II-2']} />
          </section>

          <section className="card">
            <h3 className="striphead">Where this table is used</h3>
            <p className="note">
              One table, read from four screens — so a change to the rulebook lands everywhere at
              once rather than in three of them.
            </p>
            <div className="usedby">
              <span className="chip">Keepers › My slate</span>
              <span className="chip">Keepers › Calculator</span>
              <span className="chip">History › Keeper history</span>
              <span className="chip">Draft › Draftboard</span>
            </div>
          </section>

          <section className="card">
            <h3 className="striphead">Farm graduates</h3>
            <p className="note">
              A farm player who reaches the NFL is valued at his NFL round plus ten, capped at the
              last round of the draft. Undrafted, he comes in at Round {udfa}.
            </p>
            <Citations ids={['2017-II-5']} />
          </section>

          <section className="card">
            <h3 className="striphead">Slots and tax</h3>
            <p className="note">
              {LEAGUE_2026.freeKeepers} keepers free, {LEAGUE_2026.maxKeepers} maximum. A franchise
              tag consumes three slots.
            </p>
            <table>
              <thead><tr><th className="num">Slots used</th><th className="num">Luxury tax</th></tr></thead>
              <tbody>
                {Object.entries(LEAGUE_2026.luxuryTaxByKeeperCount).map(([n, tax]) => (
                  <tr key={n}>
                    <td className="num">{n}</td>
                    <td className={`num ${tax ? '' : 'free'}`}>{tax ? faab(tax) : 'free'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </div>
      </div>
    </div>
  );
}
